import { useRef, useState } from "react";
import {
  motion,
  AnimatePresence,
  useScroll,
  useTransform,
  useMotionValue,
  useSpring,
} from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";
import { useLenis } from "lenis/react";
import { RuotaMark } from "./RuotaMark";
import { DESTINATIONS, IMAGES } from "../../data/destinations";

const EASE = [0.22, 1, 0.36, 1];

const DREAMING = DESTINATIONS.filter((d) => d.id !== "paris");

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const lenis = useLenis();
  const [preview, setPreview] = useState(0);

  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [10, -10]), { stiffness: 50, damping: 14 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-10, 10]), { stiffness: 50, damping: 14 });

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      id="top"
      ref={ref}
      onMouseMove={onMove}
      data-testid="hero-section"
      className="relative flex min-h-screen flex-col overflow-hidden bg-[#0D1F23] text-[#F7F0E3]"
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0 -z-0">
        <img
          src={IMAGES.hero}
          alt="Boats drifting below the colorful cliffside houses of Positano, Italy"
          className="h-[115%] w-full object-cover opacity-90 brightness-125 saturate-[1.15]"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0D1F23]/45 via-[#0D1F23]/10 to-[#0D1F23]/85" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0D1F23]/55 via-[#0D1F23]/20 to-transparent" />
      </motion.div>

      <motion.div
        style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
        className="pointer-events-none absolute -right-24 top-1/2 hidden -translate-y-1/2 lg:block"
      >
        <motion.div
          animate={{ y: [0, -16, 0] }}
          transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
        >
          <RuotaMark size={460} duration={40} className="opacity-70" />
        </motion.div>
      </motion.div>

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 mx-auto flex w-full max-w-7xl flex-1 flex-col justify-center px-6 pt-28 sm:px-10 lg:px-16"
      >
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.2 }}
          className="font-accent text-lg font-bold uppercase tracking-[0.25em] text-[#E2725B] [text-shadow:0_1px_14px_rgba(13,31,35,0.55)] sm:text-xl"
        >
          A Boutique Travel Advisory
        </motion.p>

        <h1
          data-testid="hero-headline"
          className="mt-6 font-display text-5xl font-light leading-[1.02] tracking-tight [text-shadow:0_2px_24px_rgba(13,31,35,0.55)] sm:text-7xl lg:text-8xl"
        >
          <MaskedLine delay={0.45}>YOUR JOURNEY</MaskedLine>
          <MaskedLine delay={0.65}>
            IN <span className="italic text-[#E2725B]">MOTION</span>
          </MaskedLine>
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.3, ease: EASE }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-explore-button"
            onClick={() => lenis?.scrollTo("#enquiry", { offset: -60, duration: 1.6 })}
            className="group flex items-center gap-3 rounded-full bg-[#168A91] px-7 py-3.5 font-accent text-[11px] uppercase tracking-[0.2em] text-[#F7F0E3] transition-colors duration-300 hover:bg-[#E2725B] hover:text-[#0D1F23]"
          >
            Begin Here
            <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
          </button>
          <button
            data-testid="hero-destinations-button"
            onClick={() => lenis?.scrollTo("#destinations", { offset: -60, duration: 1.6 })}
            className="rounded-full border border-[#F7F0E3]/30 px-7 py-3.5 font-accent text-[11px] uppercase tracking-[0.2em] transition-colors duration-300 hover:border-[#E2725B] hover:text-[#E2725B]"
          >
            Explore Inspiration
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.6 }}
          className="mt-16 flex items-center gap-5 pb-24"
        >
          <div className="relative h-14 w-14 overflow-hidden rounded-full border border-[#E2725B]/50">
            <AnimatePresence mode="wait">
              <motion.img
                key={preview}
                src={DREAMING[preview].image}
                alt={DREAMING[preview].name}
                initial={{ opacity: 0, scale: 1.15 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.6 }}
                className="h-full w-full object-cover"
              />
            </AnimatePresence>
          </div>
          <div>
            <p className="font-accent text-xs font-medium uppercase tracking-[0.3em] text-[#E2725B] [text-shadow:0_2px_10px_rgba(13,31,35,0.9)]">
              Now dreaming of
            </p>
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-2">
              {DREAMING.map((d, i) => (
                <button
                  key={d.id}
                  data-testid={`hero-preview-${d.id}`}
                  onClick={() => setPreview(i)}
                  className={`font-display text-2xl italic transition-colors duration-300 [text-shadow:0_2px_12px_rgba(13,31,35,0.85)] sm:text-3xl ${
                    i === preview ? "font-medium text-[#E2725B]" : "text-[#F7F0E3]/75 hover:text-[#F7F0E3]"
                  }`}
                >
                  {d.name}
                </button>
              ))}
            </div>
          </div>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-6 left-1/2 z-10 -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          className="flex flex-col items-center gap-2 text-[#F7F0E3]/60"
        >
          <span className="font-accent text-[10px] uppercase tracking-[0.35em]">Scroll</span>
          <ArrowDown size={14} />
        </motion.div>
      </motion.div>
    </section>
  );
};
